import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, MessageSquare, ChevronDown, ChevronUp, ImageIcon, Pin } from "lucide-react";

export default function AdminCommunity() {
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [mode, setMode] = useState("auto");

  const loadPosts = () => api.get("/community/moderation/pending").then((r) => setPendingPosts(r.data));
  const loadComments = () => api.get("/community/moderation/pending-comments").then((r) => setPendingComments(r.data));
  const loadSettings = () => api.get("/community/moderation/settings").then((r) => setMode(r.data.moderation));
  const loadAll = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    return api.get("/community/moderation/all-posts", { params }).then((r) => setAllPosts(r.data));
  };

  useEffect(() => { loadPosts(); loadComments(); loadSettings(); }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [statusFilter]);

  const approvePost = async (id) => { await api.post(`/community/moderation/${id}/approve`); toast.success("تم قبول المنشور"); loadPosts(); loadAll(); };
  const rejectPost = async (id) => { await api.post(`/community/moderation/${id}/reject`); toast.success("تم رفض المنشور"); loadPosts(); loadAll(); };
  const approveComment = async (id) => { await api.post(`/community/moderation/comments/${id}/approve`); toast.success("تم قبول التعليق"); loadComments(); };
  const rejectComment = async (id) => { await api.post(`/community/moderation/comments/${id}/reject`); toast.success("تم حذف التعليق"); loadComments(); };

  const deletePost = async (id, title = "") => {
    if (!window.confirm(`حذف نهائي للمنشور${title ? ` "${title.slice(0, 40)}..."` : ""} وجميع تعليقاته؟`)) return;
    await api.delete(`/community/moderation/posts/${id}`);
    toast.success("تم الحذف نهائياً");
    loadAll(); loadPosts(); loadComments();
  };
  const deleteComment = async (id) => {
    if (!window.confirm("حذف نهائي لهذا التعليق؟")) return;
    await api.delete(`/community/moderation/comments/${id}`);
    toast.success("تم الحذف");
    loadAll();
  };

  const changeMode = async (v) => {
    await api.post("/community/moderation/settings", { moderation: v });
    setMode(v); toast.success("تم الحفظ");
  };

  return (
    <div className="space-y-6" data-testid="admin-community">
      <h1 className="text-3xl font-display font-semibold">إدارة المجتمع</h1>

      <Card className="p-5" data-testid="moderation-settings">
        <h2 className="font-display font-semibold mb-3">إعدادات المراجعة</h2>
        <Select value={mode} onValueChange={changeMode}>
          <SelectTrigger className="w-64" data-testid="mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">نشر تلقائي</SelectItem>
            <SelectItem value="manual">مراجعة يدوية قبل النشر</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
          في وضع "المراجعة اليدوية": الأعضاء يرون منشوراتهم وتعليقاتهم فور نشرها (تظهر لهم كأنها منشورة عادية)،
          لكنها لا تصل لبقية الأعضاء إلا بعد قبولك من هنا.
        </p>
      </Card>

      {pendingPosts.length > 0 && (
        <Card className="p-5" data-testid="pending-posts-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold">منشورات معلقة</h2>
            <Badge className="bg-amber-500 hover:bg-amber-500">{pendingPosts.length}</Badge>
          </div>
          <div className="space-y-3">
            {pendingPosts.map((p) => (
              <div key={p.id} className="border rounded-lg p-4" data-testid={`pending-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">{p.author?.full_name} — <span className="opacity-70 text-xs">{p.author?.email}</span></span>
                  <Badge variant="outline">قيد المراجعة</Badge>
                </div>
                <p className="text-sm mb-3 whitespace-pre-wrap">{p.body}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approvePost(p.id)} data-testid={`btn-approve-${p.id}`}>قبول</Button>
                  <Button size="sm" variant="destructive" onClick={() => rejectPost(p.id)} data-testid={`btn-reject-${p.id}`}>رفض</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {pendingComments.length > 0 && (
        <Card className="p-5" data-testid="pending-comments-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold">تعليقات معلقة</h2>
            <Badge className="bg-amber-500 hover:bg-amber-500">{pendingComments.length}</Badge>
          </div>
          <div className="space-y-3">
            {pendingComments.map((c) => (
              <div key={c.id} className="border rounded-lg p-4" data-testid={`pending-comment-${c.id}`}>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <span className="text-sm">{c.author?.full_name} — <span className="opacity-70 text-xs">{c.author?.email}</span></span>
                  <Badge variant="outline">قيد المراجعة</Badge>
                </div>
                {c.post_preview && (
                  <div className="text-[11px] text-muted-foreground mb-2 line-clamp-1">
                    على المنشور: <span className="italic">"{c.post_preview}"</span>
                  </div>
                )}
                <p className="text-sm mb-3 whitespace-pre-wrap bg-muted/50 rounded p-2">{c.body}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approveComment(c.id)} data-testid={`btn-approve-comment-${c.id}`}>قبول</Button>
                  <Button size="sm" variant="destructive" onClick={() => rejectComment(c.id)} data-testid={`btn-reject-comment-${c.id}`}>حذف</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* All posts — browse & delete anything */}
      <Card className="p-5" data-testid="all-posts-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-semibold">كل المنشورات</h2>
            <Badge variant="outline">{allPosts.length}</Badge>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="approved">معتمد</SelectItem>
              <SelectItem value="auto">منشور تلقائي</SelectItem>
              <SelectItem value="pending">قيد المراجعة</SelectItem>
              <SelectItem value="rejected">مرفوض</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {allPosts.length === 0 ? (
          <p className="opacity-60 text-sm text-center py-8">لا منشورات</p>
        ) : (
          <div className="space-y-3">
            {allPosts.map((p) => (
              <PostRow key={p.id} post={p} onDelete={deletePost} onDeleteComment={deleteComment} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function statusBadge(status) {
  const map = {
    approved: { l: "معتمد", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    auto:     { l: "منشور", cls: "bg-blue-100 text-blue-800 border-blue-300" },
    pending:  { l: "قيد المراجعة", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    rejected: { l: "مرفوض", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  };
  const s = map[status] || { l: status || "—", cls: "bg-muted text-foreground/70 border-foreground/20" };
  return <Badge variant="outline" className={`text-[10px] ${s.cls}`}>{s.l}</Badge>;
}

function PostRow({ post, onDelete, onDeleteComment }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!expanded && comments === null) {
      setLoading(true);
      try {
        const { data } = await api.get(`/community/moderation/posts/${post.id}/comments`);
        setComments(data);
      } catch { setComments([]); }
      finally { setLoading(false); }
    }
    setExpanded(!expanded);
  };

  const removeComment = async (id) => {
    await onDeleteComment(id);
    setComments((prev) => (prev || []).filter((c) => c.id !== id));
  };

  return (
    <div className="border rounded-lg p-4" data-testid={`all-post-${post.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{post.author?.full_name || "—"}</span>
            <span className="text-xs text-muted-foreground truncate">{post.author?.email}</span>
            {statusBadge(post.status)}
            {post.pinned && <Pin className="h-3 w-3 text-amber-500" />}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {new Date(post.created_at).toLocaleString("ar")}
            {post.category && ` · ${post.category}`}
            {typeof post.comment_count === "number" && ` · 💬 ${post.comment_count} تعليق`}
          </div>
        </div>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onDelete(post.id, post.body || "")}
          className="gap-1"
          data-testid={`btn-delete-post-${post.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" /> حذف
        </Button>
      </div>

      <p className="text-sm whitespace-pre-wrap leading-relaxed mb-2">{post.body}</p>

      {(post.images || []).length > 0 && (
        <div className="flex gap-2 flex-wrap mb-2">
          {post.images.map((img, i) => (
            <a key={i} href={img} target="_blank" rel="noreferrer" className="relative">
              <img src={img} alt="" className="h-16 w-16 rounded object-cover border" />
              <ImageIcon className="absolute bottom-0.5 end-0.5 h-3 w-3 text-white drop-shadow" />
            </a>
          ))}
        </div>
      )}

      <button
        onClick={toggle}
        className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
        data-testid={`btn-toggle-comments-${post.id}`}
      >
        <MessageSquare className="h-3.5 w-3.5" />
        {expanded ? "إخفاء التعليقات" : `عرض التعليقات (${post.comment_count || 0})`}
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 pt-3 border-t">
          {loading ? (
            <div className="text-xs text-muted-foreground">جاري التحميل...</div>
          ) : (comments || []).length === 0 ? (
            <div className="text-xs text-muted-foreground italic">لا توجد تعليقات على هذا المنشور.</div>
          ) : (comments || []).map((c) => (
            <div key={c.id} className="bg-muted/40 rounded-md p-2.5 text-xs" data-testid={`comment-${c.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <span className="font-medium">{c.author?.full_name || "—"}</span>
                <div className="flex items-center gap-2">
                  {statusBadge(c.status)}
                  <button
                    onClick={() => removeComment(c.id)}
                    className="text-destructive hover:text-destructive/80"
                    aria-label="حذف التعليق"
                    data-testid={`btn-delete-comment-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="whitespace-pre-wrap leading-relaxed">{c.body}</p>
              <div className="text-[10px] text-muted-foreground mt-1">
                {new Date(c.created_at).toLocaleString("ar")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
